import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import PagamentoMensalidadeModal from '../components/PagamentoMensalidadeModal';
import './Profile.css';

const SECTIONS = [
  { id: 'dados',        icon: 'fas fa-user',           label: 'Meus Dados',       adminOnly: false, alunoOnly: false },
  { id: 'foto',         icon: 'fas fa-camera',          label: 'Foto de Perfil',   adminOnly: false, alunoOnly: false },
  { id: 'favoritos',    icon: 'fas fa-heart',           label: 'Favoritos',        adminOnly: false, alunoOnly: false },
  { id: 'notificacoes', icon: 'fas fa-bell',            label: 'Notificações',     adminOnly: false, alunoOnly: false },
  { id: 'pagamentos',   icon: 'fas fa-euro-sign',       label: 'Meus Pagamentos',  adminOnly: false, alunoOnly: true  },
  { id: 'anotacoes',    icon: 'fas fa-sticky-note',     label: 'Anotações',        adminOnly: false, alunoOnly: false },
  { id: 'cursos',       icon: 'fas fa-graduation-cap',  label: 'Meus Cursos',      adminOnly: false, alunoOnly: false },
  { id: 'ajuda',        icon: 'fas fa-question-circle', label: 'Ajuda',            adminOnly: false, alunoOnly: false },
  { id: 'admin',        icon: 'fas fa-shield-alt',      label: 'Painel Admin',     adminOnly: true,  alunoOnly: false },
];

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('pt-BR', { day:'2-digit', month:'short', year:'numeric' });
}

function truncate(str, n = 100) {
  if (!str) return '';
  return str.length > n ? str.slice(0, n) + '...' : str;
}

export default function Profile() {
  const { logout, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [profile,       setProfile]       = useState(null);
  const [activeSection, setActiveSection] = useState(
    location.state?.section ?? 'dados'
  );
  const [collapsed,     setCollapsed]     = useState(false);
  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [toast,         setToast]         = useState(null);

  // Section data
  const [favorites,      setFavorites]     = useState([]);
  const [notifications,  setNotifications] = useState([]);
  const [notes,          setNotes]         = useState([]);
  const [courses,        setCourses]       = useState([]);
  const [pagamentos,     setPagamentos]    = useState(null);
  const [showPagModal,   setShowPagModal]  = useState(false);

  // Photo
  const [photoFile,     setPhotoFile]     = useState(null);
  const [photoPreview,  setPhotoPreview]  = useState(null);
  const [uploading,     setUploading]     = useState(false);
  const fileInputRef = useRef();

  // Notes
  const [noteText,   setNoteText]   = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const toastTimer = useRef(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }

  const [expandedId, setExpandedId] = useState(null);

  function toggleExpand(id) {
    setExpandedId(prev => prev === id ? null : id);
  }

  // Load profile
  useEffect(() => {
    api.get('/user/me').then(r => setProfile(r.data)).catch(() => {});
  }, []);

  // Carrega notificações no mount para exibir o badge no sidebar
  useEffect(() => {
    api.get('/user/notifications').then(r => setNotifications(r.data)).catch(() => {});
  }, []);

  // Load section data on switch
  useEffect(() => {
    if (activeSection === 'favoritos' && favorites.length === 0) {
      api.get('/user/favorites').then(r => setFavorites(r.data)).catch(() => {});
    }
    if (activeSection === 'notificacoes' && notifications.length === 0) {
      api.get('/user/notifications').then(r => setNotifications(r.data)).catch(() => {});
    }
    if (activeSection === 'anotacoes' && notes.length === 0) {
      api.get('/user/notes').then(r => setNotes(r.data)).catch(() => {});
    }
    if (activeSection === 'cursos' && courses.length === 0) {
      api.get('/user/courses').then(r => setCourses(r.data)).catch(() => {});
    }
    if (activeSection === 'pagamentos' && !pagamentos) {
      api.get('/user/pagamentos').then(r => setPagamentos(r.data)).catch(() => {});
    }
  }, [activeSection]); // eslint-disable-line

  // Confirma pagamento de mensalidade ao retornar de MP/PayPal/Stripe
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mpPaymentId      = params.get('payment_id') || params.get('collection_id');
    const paypalToken      = params.get('token');
    const stripeSessionId  = params.get('stripe_session_id');
    const pagamentoStatus  = params.get('pagamento');

    async function confirmar() {
      try {
        if (mpPaymentId) {
          await api.get(`/pagamentos/mensalidade/cartao/confirmar?payment_id=${mpPaymentId}`);
          showToast('Pagamento confirmado!');
        } else if (paypalToken) {
          // Captura já ocorre no backend (redirect); apenas avisa e recarrega.
          showToast('Pagamento confirmado!');
        } else if (stripeSessionId) {
          await api.get(`/pagamentos/mensalidade/stripe/confirmar?session_id=${stripeSessionId}`);
          showToast('Pagamento confirmado!');
        } else if (pagamentoStatus === 'ok') {
          showToast('Pagamento confirmado!');
        } else if (pagamentoStatus === 'erro' || pagamentoStatus === 'cancelado') {
          showToast('Pagamento não foi concluído.', 'error');
        } else {
          return;
        }
        setPagamentos(null);
        setActiveSection('pagamentos');
        navigate('/usuarios', { replace: true });
      } catch {
        showToast('Não foi possível confirmar o pagamento.', 'error');
      }
    }

    if (mpPaymentId || paypalToken || stripeSessionId || pagamentoStatus) confirmar();
  }, []); // eslint-disable-line

  // Photo preview
  function handleFileChange(e) {
    const f = e.target.files[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  }

  async function handlePhotoUpload() {
    if (!photoFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('imagem', photoFile);
    try {
      const res = await fetch('/profile/upload-photo', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setProfile(p => ({ ...p, usuario_image: data.imageUrl }));
      setUser(prev => ({ ...prev, usuario_image: data.imageUrl }));
      setPhotoFile(null);
      setPhotoPreview(null);
      showToast('Foto atualizada com sucesso!');
    } catch {
      showToast('Erro ao enviar foto.', 'error');
    } finally {
      setUploading(false);
    }
  }

  // Unfavorite
  async function unfavorite(postId) {
    try {
      await api.post(`/user/unfavorite/${postId}`);
      setFavorites(prev => prev.filter(f => f.id !== postId));
      showToast('Post removido dos favoritos.');
    } catch {
      showToast('Erro ao desfavoritar.', 'error');
    }
  }

  // Mark notification as read
  async function markRead(id) {
    try {
      await api.post(`/user/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, lida: true } : n));
    } catch {}
  }

  // Notes CRUD
  async function saveNote(e) {
    e.preventDefault();
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await api.post('/user/notes', { content: noteText });
      const r = await api.get('/user/notes');
      setNotes(r.data);
      setNoteText('');
      showToast('Anotação salva!');
    } catch {
      showToast('Erro ao salvar anotação.', 'error');
    } finally {
      setSavingNote(false);
    }
  }

  async function deleteNote(id) {
    try {
      await api.delete(`/user/notes/${id}`);
      setNotes(prev => prev.filter(n => n.id !== id));
      showToast('Anotação excluída.');
    } catch {
      showToast('Erro ao excluir.', 'error');
    }
  }

  // Logout
  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  function selectSection(id) {
    setActiveSection(id);
    setMobileOpen(false);
  }

  const avatarSrc = photoPreview
    || (profile?.usuario_image ? profile.usuario_image : '/images/default-avatar.png');

  // ── Render section content ──
  function SectionContent() {
    switch (activeSection) {

      case 'dados': return (
        <div>
          <h2 className="profile-section-title"><i className="fas fa-user" /> Meus Dados</h2>
          <div className="profile-dados-card">
            <div className="profile-field">
              <div className="profile-field-label">Nome</div>
              <div className="profile-field-value">{profile?.nome || '—'}</div>
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Email</div>
              <div className="profile-field-value">{profile?.email || '—'}</div>
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Tipo de conta</div>
              <div className="profile-field-value">
                {profile?.isAdmin
                  ? <span className="profile-badge admin"><i className="fas fa-shield-alt" /> Administrador</span>
                  : profile?.isAluno
                    ? <span className="profile-badge aluno"><i className="fas fa-graduation-cap" /> Aluno</span>
                    : <span className="profile-badge basico"><i className="fas fa-user" /> Básico</span>
                }
              </div>
            </div>
          </div>
        </div>
      );

      case 'foto': return (
        <div>
          <h2 className="profile-section-title"><i className="fas fa-camera" /> Foto de Perfil</h2>
          <div className="profile-foto-card">
            <img src={avatarSrc} alt="Foto de perfil" className="profile-foto-preview"
                 onError={e => { e.target.src = '/images/default-avatar.png'; }} />

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            <label
              className="profile-foto-upload-label"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="fas fa-cloud-upload-alt" />
              <span>{photoFile ? photoFile.name : 'Clique para escolher uma foto'}</span>
              <small style={{ color: '#bbb', fontWeight: 400 }}>JPG, PNG, GIF · Máx. 50MB</small>
            </label>

            {photoFile && (
              <button className="profile-btn" onClick={handlePhotoUpload} disabled={uploading}>
                <i className="fas fa-check" />
                {uploading ? 'Enviando...' : 'Salvar foto'}
              </button>
            )}
          </div>
        </div>
      );

      case 'favoritos': return (
        <div>
          <h2 className="profile-section-title"><i className="fas fa-heart" /> Favoritos</h2>
          {favorites.length === 0
            ? <p style={{ color: '#90a4ae', padding: '20px 0' }}>Nenhum post favoritado ainda.</p>
            : <div className="profile-list">
                {favorites.map(f => (
                  <div key={f.id} className="profile-list-item">
                    <div className="profile-list-item-body">
                      <Link
                        to="/blog"
                        state={{ openPostId: f.id }}
                        className="profile-list-item-title"
                        style={{ color: '#1e90ff', textDecoration: 'none', display: 'block' }}
                        title="Ir para o post"
                      >
                        <i className="fas fa-external-link-alt" style={{ marginRight: 6, fontSize: '0.75rem' }} />
                        {f.title || f.titulo || 'Post'}
                      </Link>
                      <div className="profile-list-item-meta">
                        <i className="fas fa-calendar" style={{ marginRight: 4 }} />
                        {formatDate(f.created_at)}
                      </div>
                    </div>
                    <button className="profile-icon-btn heart" title="Desfavoritar"
                            onClick={() => unfavorite(f.id)}>
                      <i className="fas fa-heart-broken" />
                    </button>
                  </div>
                ))}
              </div>
          }
        </div>
      );

      case 'notificacoes': return (
        <div>
          <h2 className="profile-section-title">
            <i className="fas fa-bell" /> Notificações
            {notifications.filter(n => !n.lida).length > 0 && (
              <span style={{ background: '#e53935', color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700 }}>
                {notifications.filter(n => !n.lida).length} nova{notifications.filter(n => !n.lida).length > 1 ? 's' : ''}
              </span>
            )}
          </h2>
          {notifications.length === 0
            ? <p style={{ color: '#90a4ae', padding: '20px 0' }}>Nenhuma notificação.</p>
            : <div className="profile-list">
                {notifications.map(n => {
                  const isExpanded = expandedId === n.id;
                  return (
                    <div
                      key={n.id}
                      className={`profile-list-item profile-notif-item${!n.lida ? ' unread' : ''}${isExpanded ? ' expanded' : ''}`}
                      onClick={() => {
                        toggleExpand(n.id);
                        if (!n.lida) markRead(n.id);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div style={{ flexShrink: 0, color: n.lida ? '#90a4ae' : '#4f46e5', fontSize: '1.1rem', marginTop: 2 }}>
                        <i className={`fas fa-bell${n.lida ? '' : ' fa-shake'}`} />
                      </div>
                      <div className="profile-list-item-body">
                        <div className={`profile-notif-text${isExpanded ? ' expanded' : ''}`}>
                          {isExpanded ? n.mensagem : truncate(n.mensagem, 120)}
                        </div>
                        <div className="profile-list-item-meta">
                          <i className="fas fa-clock" style={{ marginRight: 4 }} />
                          {formatDate(n.data_envio)}
                          {!n.lida && <span style={{ marginLeft: 10, color: '#4f46e5', fontWeight: 700, fontSize: '0.75rem' }}>● Não lida</span>}
                          <span style={{ marginLeft: 10, color: '#90a4ae', fontSize: '0.72rem' }}>
                            {isExpanded ? '▲ recolher' : '▼ ver mais'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
          }
        </div>
      );

      case 'pagamentos': return (
        <div>
          <h2 className="profile-section-title">
            <i className="fas fa-euro-sign" /> Meus Pagamentos
          </h2>

          {!pagamentos ? (
            <p style={{ color: '#90a4ae', padding: '20px 0' }}>Carregando...</p>
          ) : !pagamentos.mensalidade ? (
            <p style={{ color: '#90a4ae', padding: '20px 0' }}>
              Nenhuma mensalidade configurada ainda. Fale com o seu professor.
            </p>
          ) : (
            <>
              {/* Card da mensalidade atual */}
              <div style={{ background: '#f0f4ff', border: '1.5px solid #c7d2fe', borderRadius: 14, padding: '18px 22px', marginBottom: 28, display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#4f46e5' }}>
                    {new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(pagamentos.mensalidade.valor)}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6366f1', marginTop: 2 }}>mensalidade</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#4f46e5' }}>
                    Dia {pagamentos.mensalidade.dia_vencimento}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6366f1', marginTop: 2 }}>vencimento</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#4f46e5' }}>
                    {new Date(String(pagamentos.mensalidade.data_proximo_vencimento).split('T')[0] + 'T00:00:00').toLocaleDateString('pt-BR')}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6366f1', marginTop: 2 }}>próximo vencimento</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                  <button
                    onClick={() => setShowPagModal(true)}
                    style={{
                      background: 'linear-gradient(135deg, #4f46e5, #4338ca)',
                      color: '#fff', border: 'none', borderRadius: 24,
                      padding: '12px 26px', fontWeight: 700, fontSize: '0.92rem',
                      cursor: 'pointer', fontFamily: 'Poppins, sans-serif',
                      display: 'flex', alignItems: 'center', gap: 8,
                      boxShadow: '0 4px 14px rgba(79,70,229,0.35)',
                    }}
                  >
                    <i className="fas fa-wallet" /> Pagar Agora
                  </button>
                </div>
              </div>

              {showPagModal && (
                <PagamentoMensalidadeModal
                  onClose={() => setShowPagModal(false)}
                  onPaid={() => {
                    setShowPagModal(false);
                    showToast('Pagamento confirmado!');
                    setPagamentos(null);
                    api.get('/user/pagamentos').then(r => setPagamentos(r.data)).catch(() => {});
                  }}
                />
              )}

              {/* Histórico */}
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#475569', marginBottom: 14 }}>
                <i className="fas fa-history" style={{ marginRight: 6, color: '#4f46e5' }} />
                Histórico de pagamentos
              </h3>

              {pagamentos.historico.length === 0 ? (
                <p style={{ color: '#90a4ae', fontSize: '0.88rem' }}>Nenhum pagamento registrado ainda.</p>
              ) : (
                <div className="profile-list">
                  {pagamentos.historico.map((p, i) => {
                    const ref = new Date(String(p.referencia).split('T')[0] + 'T00:00:00');
                    const mes = ref.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
                    const confirmado = new Date(String(p.data_confirmacao).split('T')[0] + 'T00:00:00').toLocaleDateString('pt-BR');
                    return (
                      <div key={i} className="profile-list-item" style={{ borderLeft: '4px solid #10b981' }}>
                        <div style={{ flexShrink: 0, color: '#10b981', fontSize: '1.2rem', marginTop: 2 }}>
                          <i className="fas fa-check-circle" />
                        </div>
                        <div className="profile-list-item-body">
                          <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '0.95rem', textTransform: 'capitalize' }}>
                            {mes}
                          </div>
                          <div className="profile-list-item-meta">
                            <i className="fas fa-calendar-check" style={{ marginRight: 4 }} />
                            Confirmado em {confirmado}
                          </div>
                        </div>
                        <div style={{ fontWeight: 800, color: '#4f46e5', fontSize: '1.05rem', flexShrink: 0 }}>
                          {new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(p.valor)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      );

      case 'anotacoes': return (
        <div>
          <h2 className="profile-section-title"><i className="fas fa-sticky-note" /> Anotações</h2>

          {/* Form */}
          <form className="profile-note-form" onSubmit={saveNote} style={{ marginBottom: 24 }}>
            <textarea
              placeholder="Escreva uma nova anotação..."
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              required
            />
            <div>
              <button type="submit" className="profile-btn" disabled={savingNote}>
                <i className="fas fa-plus" />
                {savingNote ? 'Salvando...' : 'Adicionar anotação'}
              </button>
            </div>
          </form>

          {notes.length === 0
            ? <p style={{ color: '#90a4ae' }}>Nenhuma anotação ainda.</p>
            : <div className="profile-notes-grid">
                {notes.map(n => (
                  <div key={n.id} className="profile-note-card">
                    <button className="profile-note-delete-btn" title="Excluir"
                            onClick={() => deleteNote(n.id)}>
                      <i className="fas fa-trash" />
                    </button>
                    <p className="profile-note-card-text">{truncate(n.texto, 140)}</p>
                    <div className="profile-note-card-date">
                      <i className="fas fa-clock" style={{ marginRight: 4 }} />
                      {formatDate(n.created_at)}
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>
      );

      case 'cursos': return (
        <div>
          <h2 className="profile-section-title"><i className="fas fa-graduation-cap" /> Meus Cursos</h2>
          {courses.length === 0
            ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#90a4ae' }}>
                <i className="fas fa-book-open" style={{ fontSize: '3rem', marginBottom: 12, display: 'block' }} />
                <p>Você ainda não tem cursos.</p>
                <a href="/produtos" style={{ color: '#4f46e5', fontWeight: 600, textDecoration: 'none' }}>
                  Ver cursos disponíveis →
                </a>
              </div>
            )
            : <div className="profile-courses-grid">
                {courses.map(c => (
                  <div key={c.id} className="profile-course-card">
                    <img src={`/uploads/produtos/${c.imagem}`} alt={c.nome}
                         onError={e => { e.target.style.display = 'none'; }} />
                    <div className="profile-course-body">
                      <h4>{c.nome}</h4>
                      {c.descricao && <p>{truncate(c.descricao, 80)}</p>}
                      <a href={`/produto/${c.id}`} style={{ color: '#4f46e5', fontSize: '0.82rem', fontWeight: 600 }}>
                        Acessar curso →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>
      );

      case 'ajuda': return (
        <div>
          <h2 className="profile-section-title"><i className="fas fa-question-circle" /> Ajuda & Suporte</h2>
          <div className="profile-dados-card">
            <p style={{ color: '#666', marginBottom: 20, fontWeight: 400, fontSize: '0.95rem' }}>
              Precisa de ajuda? Entre em contato pelos canais abaixo:
            </p>
            {[
              { icon: 'fas fa-envelope', label: 'E-mail', value: 'dionisiodacostaelias@outlook.com', href: 'mailto:dionisiodacostaelias@outlook.com' },
              { icon: 'fab fa-whatsapp', label: 'WhatsApp', value: '+55 (92) 98626-9440', href: 'https://wa.me/5592986269440' },
            ].map(c => (
              <div key={c.label} className="profile-field" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <i className={c.icon} style={{ color: '#4f46e5', fontSize: '1.2rem', flexShrink: 0 }} />
                <div>
                  <div className="profile-field-label">{c.label}</div>
                  <a href={c.href} target="_blank" rel="noreferrer"
                     style={{ color: '#4f46e5', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem' }}>
                    {c.value}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

      case 'admin': return (
        <div>
          <h2 className="profile-section-title">
            <i className="fas fa-shield-alt" /> Painel de Administração
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14 }}>
            {[
              { href: '/admin-dashboard',       icon: 'fas fa-chart-line',    label: 'Dashboard',             color: '#0ea5e9', bg: '#e0f2fe' },
              { href: '/admin-blog',           icon: 'fas fa-newspaper',     label: 'Gerenciar Blog',        color: '#ef4444', bg: '#fee2e2' },
              { href: '/admin-textos',          icon: 'fas fa-book-reader',   label: 'Gerenciar Textos',      color: '#8b5cf6', bg: '#ede9fe' },
              { href: '/admin-vocabulario',     icon: 'fas fa-spell-check',   label: 'Gerenciar Vocabulário', color: '#ff6b6b', bg: '#fee2e2' },
              { href: '/admin/produtos',        icon: 'fas fa-box-open',      label: 'Gerenciar Produtos',    color: '#1e90ff', bg: '#dbeafe' },
              { href: '/admin-users',           icon: 'fas fa-users-cog',     label: 'Gerenciar Usuários',    color: '#f59e0b', bg: '#fef3c7' },
              { href: '/admin-alunos',          icon: 'fas fa-graduation-cap', label: 'Gerenciar Alunos',      color: '#4f46e5', bg: '#ede9fe' },
              { href: '/admin-notificacoes',    icon: 'fas fa-bell',          label: 'Enviar Notificações',   color: '#10b981', bg: '#d1fae5' },
              { href: '/admin/board',           icon: 'fas fa-chalkboard',    label: 'Quadro Virtual',        color: '#6d28d9', bg: '#ede9fe' },
              { href: '/admin/testimonials',    icon: 'fas fa-comment-dots',  label: 'Depoimentos',           color: '#059669', bg: '#d1fae5' },
              { href: '/admin/planos',          icon: 'fas fa-credit-card',   label: 'Planos & Assinaturas',  color: '#0284c7', bg: '#e0f2fe' },
            ].map(item => (
              <a
                key={item.href}
                href={item.href}
                className="profile-admin-card"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '16px 18px',
                  background: '#fff',
                  border: `1.5px solid ${item.color}22`,
                  borderRadius: 14,
                  textDecoration: 'none',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 8px 20px ${item.color}25`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.06)'; }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: item.bg, color: item.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.15rem', flexShrink: 0,
                }}>
                  <i className={item.icon} />
                </div>
                <span style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>
                  {item.label}
                </span>
                <i className="fas fa-arrow-right" style={{ marginLeft: 'auto', color: '#cbd5e1', fontSize: '0.8rem' }} />
              </a>
            ))}
          </div>

          {/* Badge de segurança */}
          <div style={{
            marginTop: 24, padding: '10px 16px',
            background: '#f0fdf4', border: '1px solid #bbf7d0',
            borderRadius: 10, display: 'inline-flex', alignItems: 'center', gap: 8,
            fontSize: '0.82rem', color: '#16a34a', fontWeight: 600,
          }}>
            <i className="fas fa-lock" /> Sessão segura — acesso de administrador
          </div>
        </div>
      );

      default: return null;
    }
  }

  return (
    <div className="profile-page">

      {/* Mobile overlay */}
      <div className={`profile-sidebar-overlay${mobileOpen ? ' open' : ''}`}
           onClick={() => setMobileOpen(false)} />

      {/* Sidebar */}
      <aside className={`profile-sidebar${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}>

        {/* Desktop collapse toggle */}
        <button className="profile-sidebar-toggle" onClick={() => setCollapsed(c => !c)}>
          <i className="fas fa-chevron-left" />
        </button>

        {/* User block */}
        <div className="profile-sidebar-user">
          <div className="profile-avatar-wrap" onClick={() => selectSection('foto')}>
            <img
              src={avatarSrc}
              alt="Avatar"
              className="profile-avatar"
              onError={e => { e.target.src = '/images/default-avatar.png'; }}
            />
            <div className="profile-avatar-edit"><i className="fas fa-pen" /></div>
          </div>
          <span className="profile-sidebar-name">{profile?.nome || '...'}</span>
          <span className="profile-sidebar-email">{profile?.email || ''}</span>
        </div>

        {/* Navigation */}
        <nav className="profile-sidebar-nav">
          {SECTIONS.filter(s => {
            if (s.adminOnly && !profile?.isAdmin) return false;
            if (s.alunoOnly && !profile?.isAluno) return false;
            return true;
          }).map(s => (
            <button
              key={s.id}
              className={`profile-nav-item${activeSection === s.id ? ' active' : ''}`}
              onClick={() => selectSection(s.id)}
              title={s.label}
            >
              <i className={s.icon} />
              <span className="profile-nav-label">{s.label}</span>
              {s.id === 'notificacoes' && notifications.filter(n => !n.lida).length > 0 && (
                <span className="profile-nav-badge">
                  {notifications.filter(n => !n.lida).length}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Logout */}
        <div className="profile-sidebar-bottom">
          <button className="profile-logout-btn" onClick={handleLogout} title="Sair">
            <i className="fas fa-sign-out-alt" />
            <span className="profile-nav-label">Sair</span>
          </button>
        </div>
      </aside>

      {/* Mobile sidebar toggle */}
      <button className="profile-mobile-toggle" onClick={() => setMobileOpen(o => !o)}>
        <i className="fas fa-bars" />
      </button>

      {/* Content */}
      <div className="profile-content">
        {!profile
          ? <div style={{ textAlign: 'center', padding: '60px 0', color: '#90a4ae' }}>
              <i className="fas fa-spinner fa-spin fa-2x" />
            </div>
          : <SectionContent />
        }
      </div>

      {/* Toast */}
      {toast && <div className={`profile-toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
